import { generateUuid } from '@lib/uuid';
import {
  blockToBlockActions,
  blockToBlockIndex,
  blockToBlockSize,
  blockToBlockUpdate,
  blockToBlockVisibility,
} from '@nx-frontend/shared/dtos';
import { useEnshureGhostBlock } from '@nx-frontend/shared/hooks';
import { useChanges } from '@nx-frontend/shared/hooks/useChangeEvents';
import {
  BlockActionsData,
  BlockIndexData,
  BlockSizeData,
  BlockVisibilityData,
  PortalBlock,
} from '@nx-frontend/shared/types';
import { ChangeEvent, ChangeEventBase, ChangeEventType } from '@nx-frontend/shared/types';
import { compareObjectsOmitProps, compareObjectsPartially } from '@nx-frontend/shared/utils';

type BlockChangePayload = Pick<
  ChangeEventBase<PortalBlock>,
  'newValue' | 'oldValue' | 'squashId' | 'collectId' | 'batchId'
>;

export const useBlockChanges = (): ((payload: BlockChangePayload) => Promise<ChangeEvent | undefined>) => {
  const setChanges = useChanges();
  const enshureGhostBlock = useEnshureGhostBlock();

  return async (payload) => {
    const { squashId, collectId, batchId = generateUuid() } = payload;

    const commonProps = {
      batchId,
      ...(squashId != null ? { squashId } : null),
      ...(collectId != null ? { collectId } : null),
    };

    await enshureGhostBlock({ block: payload.oldValue, ...commonProps });

    const oldSizeValue = blockToBlockSize(payload.oldValue);
    const newSizeValue = blockToBlockSize(payload.newValue);
    if (!compareObjectsPartially<BlockSizeData>(newSizeValue, oldSizeValue, ['colspan', 'rowspan', 'mediaSizes'])) {
      setChanges({
        ...commonProps,
        type: ChangeEventType.UPDATE_BLOCK_SIZE,
        oldValue: oldSizeValue,
        newValue: newSizeValue,
      });
    }

    const oldVisibilityValue = blockToBlockVisibility(payload.oldValue);
    const newVisibilityValue = blockToBlockVisibility(payload.newValue);
    if (!compareObjectsPartially<BlockVisibilityData>(newVisibilityValue, oldVisibilityValue, ['enabled'])) {
      setChanges({
        ...commonProps,
        type: ChangeEventType.UPDATE_BLOCK_VISIBILITY,
        oldValue: oldVisibilityValue,
        newValue: newVisibilityValue,
      });
    }

    const oldIndexValue = blockToBlockIndex(payload.oldValue);
    const newIndexValue = blockToBlockIndex(payload.newValue);
    if (!compareObjectsPartially<BlockIndexData>(oldIndexValue, newIndexValue, ['index'])) {
      setChanges({
        ...commonProps,
        type: ChangeEventType.UPDATE_BLOCK_INDEX,
        oldValue: oldIndexValue,
        newValue: newIndexValue,
      });
    }

    const oldActionsValue = blockToBlockActions(payload.oldValue);
    const newActionsValue = blockToBlockActions(payload.newValue);
    if (!compareObjectsPartially<BlockActionsData>(newActionsValue, oldActionsValue, ['actions'])) {
      setChanges({
        ...commonProps,
        type: ChangeEventType.UPDATE_BLOCK_ACTIONS,
        oldValue: oldActionsValue,
        newValue: newActionsValue,
      });
    }

    const oldBlockValue = blockToBlockUpdate(payload.oldValue);
    const newBlockValue = blockToBlockUpdate(payload.newValue);
    if (
      !compareObjectsOmitProps<Record<string, unknown>>(newBlockValue, oldBlockValue, [
        'id',
        'type',
        'place',
        'index',
        'colspan',
        'rowspan',
        'actions',
        'mediaSizes',
      ])
    ) {
      return setChanges({
        ...commonProps,
        type: ChangeEventType.UPDATE_BLOCK,
        oldValue: oldBlockValue,
        newValue: newBlockValue,
      });
    }
  };
};
